import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./CategoryAnalysis.module.scss";
import {
  Card,
  Table,
  Select,
  Input,
  Typography,
  Tag,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { getCategoryAnalysis, getAllChannels, getAllBranchOffices } from "../../../api/Services";

const { Title, Text } = Typography;

const formatCurrency = (v: number) =>
  `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;

const formatNumber = (v: number) => v.toLocaleString("es-AR");

const VariationBadge: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = "%" }) => {
  if (value === null || value === undefined) return <span>—</span>;
  const isPositive = value >= 0;
  return (
    <span className={`${styles.variation} ${isPositive ? styles.positive : styles.negative}`}>
      {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {" "}{value >= 0 ? "+" : ""}{value.toFixed(1)}{suffix}
    </span>
  );
};

const CategoryAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState<string>("30");
  const [channel, setChannel] = useState<number | undefined>(undefined);
  const [store, setStore] = useState<number | undefined>(undefined);
  const [sortField, setSortField] = useState<string>("grossSales");
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend">("descend");
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [channelOptions, setChannelOptions] = useState<{ value: number; label: string }[]>([]);
  const [storeOptions, setStoreOptions] = useState<{ value: number; label: string }[]>([]);

  useEffect(() => {
    getAllChannels().then((channels: any[]) => {
      setChannelOptions(
        channels.map((c: any) => ({ value: c.id, label: c.type?.type ?? String(c.id) }))
      );
    });
    getAllBranchOffices().then((stores) => {
      setStoreOptions(
        (stores ?? []).map((b: { id: number; name: string }) => ({ value: b.id, label: b.name }))
      );
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    getCategoryAnalysis(Number(period), channel, store)
      .then((data: any) => setCategories(data?.categories ?? []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, [period, channel, store]);

  const filtered = useMemo(() => {
    let data = [...categories];

    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((c) => (c.fullName ?? c.name ?? "").toLowerCase().includes(q));
    }

    const field = sortField as keyof typeof data[0];
    data.sort((a, b) => {
      const av = a[field] as number;
      const bv = b[field] as number;
      return sortOrder === "descend" ? bv - av : av - bv;
    });

    return data;
  }, [categories, search, sortField, sortOrder]);

  const columns = [
    {
      title: t("Common-Category"),
      dataIndex: "fullName",
      key: "fullName",
      render: (fullName: string, record: any) => (
        <div>
          <Text strong style={{ cursor: "pointer", color: "#1890ff" }}>
            {fullName ?? record.name}
          </Text>
          <Tag
            color={record.level === 1 ? "purple" : record.level === 2 ? "blue" : "default"}
            style={{ marginLeft: 8, fontSize: 10 }}
          >
            N{record.level}
          </Tag>
        </div>
      ),
    },
    {
      title: t("CategoryAnalysis-GrossSales"),
      key: "grossSales",
      sorter: true,
      sortOrder: sortField === "grossSales" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: any) => {
        const variation = r.grossSalesLY > 0 ? ((r.grossSales - r.grossSalesLY) / r.grossSalesLY) * 100 : null;
        return (
          <div className={styles.cellStack}>
            <Text strong>{formatCurrency(r.grossSales)}</Text>
            {variation !== null && <VariationBadge value={variation} />}
          </div>
        );
      },
    },
    {
      title: t("Common-Units"),
      key: "units",
      sorter: true,
      sortOrder: sortField === "units" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: any) => {
        const variation = r.unitsLY > 0 ? ((r.units - r.unitsLY) / r.unitsLY) * 100 : null;
        return (
          <div className={styles.cellStack}>
            <Text strong>{formatNumber(r.units)}</Text>
            {variation !== null && <VariationBadge value={variation} />}
          </div>
        );
      },
    },
    {
      title: t("Common-Margin"),
      key: "margin",
      sorter: true,
      sortOrder: sortField === "margin" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: any) => {
        const variation = r.margin - r.marginLY;
        return (
          <div className={styles.cellStack}>
            <Text strong>{r.margin.toFixed(1)}%</Text>
            <VariationBadge value={variation} suffix=" pp" />
          </div>
        );
      },
    },
    {
      title: t("Common-Products"),
      dataIndex: "products",
      key: "products",
      align: "right" as const,
      render: (v: number) => formatNumber(v),
    },
  ];

  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (sorter.columnKey) {
      setSortField(sorter.columnKey);
      setSortOrder(sorter.order || "descend");
    }
  };

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("CategoryAnalysis-PageTitle")}
      </Title>

      {/* ═══ FILTERS ═══ */}
      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <Input
            placeholder={t("CategoryAnalysis-SearchPlaceholder")}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
            allowClear
          />
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("Common-Period")}</Text>
            <Select
              value={period}
              onChange={setPeriod}
              className={styles.filterSelect}
              options={[
                { value: "30", label: t("CategoryAnalysis-Last30Days") },
                { value: "60", label: t("CategoryAnalysis-Last60Days") },
                { value: "90", label: t("CategoryAnalysis-Last90Days") },
                { value: "180", label: t("CategoryAnalysis-Last180Days") },
              ]}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("Common-Channel")}</Text>
            <Select
              placeholder={t("CategoryAnalysis-AllChannels")}
              allowClear
              value={channel}
              onChange={setChannel}
              className={styles.filterSelect}
              options={channelOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("Common-BranchOffice")}</Text>
            <Select
              placeholder={t("CategoryAnalysis-AllStores")}
              allowClear
              value={store}
              onChange={setStore}
              className={styles.filterSelect}
              options={storeOptions}
            />
          </div>
        </div>
      </Card>

      {/* ═══ TABLE ═══ */}
      <Card className={styles.tableCard}>
        <Table
          columns={columns}
          dataSource={filtered.map((c) => ({ ...c, key: c.id }))}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          loading={loading}
          className={styles.dataTable}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => navigate(`/commercial-analysis/categories/${record.id}`),
            style: { cursor: "pointer" },
          })}
        />
      </Card>
    </div>
  );
};

export default CategoryAnalysis;
